import { TestType, TestConfig } from '../../types/test';
import { TESTS } from '../../utils/constants';
import { AffiliateAd } from './AffiliateAd';

interface SidebarProps {
  selectedTest: TestType;
  onSelectTest: (test: TestType) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({
  selectedTest,
  onSelectTest,
  isCollapsed,
  onToggleCollapse,
  mobileOpen,
  onClose,
}: SidebarProps) {
  const coreTests = TESTS.filter((t) => t.category === 'core');
  const extendedTests = TESTS.filter((t) => t.category === 'extended');

  const handleSelect = (id: TestType) => {
    onSelectTest(id);
    onClose(); // close the drawer on mobile; harmless on desktop
  };

  const renderTestButton = (test: TestConfig) => {
    const isActive = selectedTest === test.id;
    // Burn-In Prevention is the flagship tool — give it a persistent green accent.
    const isBurnIn = test.id === 'burn-in-fix';
    const rgb = isBurnIn ? '0, 255, 136' : '0, 217, 255';

    const buttonStyle = isActive
      ? {
          background: `rgba(${rgb}, 0.12)`,
          border: `1px solid rgba(${rgb}, 0.35)`,
          boxShadow: `0 0 18px rgba(${rgb}, 0.12)`,
        }
      : isBurnIn
        ? {
            background: 'rgba(0, 255, 136, 0.05)',
            border: '1px solid rgba(0, 255, 136, 0.2)',
          }
        : {
            background: 'transparent',
            border: '1px solid transparent',
          };

    const nameColor = isActive
      ? isBurnIn ? 'text-[#00ff88]' : 'text-[#00d9ff]'
      : isBurnIn ? 'text-[#86ffc7]' : 'text-gray-300 group-hover:text-white';

    return (
      <button
        key={test.id}
        onClick={() => handleSelect(test.id)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 relative group"
        style={buttonStyle}
      >
        {!isActive && (
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ background: 'rgba(255,255,255,0.04)' }} />
        )}

        <span className={`text-xl relative z-10 ${isBurnIn ? 'drop-shadow-[0_0_6px_rgba(0,255,136,0.5)]' : ''}`}>
          {test.icon}
        </span>

        {/* On mobile the drawer is always full-width, so show labels regardless of isCollapsed */}
        <div className={`flex-1 min-w-0 relative z-10 ${isCollapsed ? 'block md:hidden' : 'block'}`}>
          <div className="flex items-center justify-between gap-2">
            <span className={`font-medium truncate text-sm transition-colors ${nameColor}`}>
              {test.name}
            </span>
            {isBurnIn ? (
              <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background: 'rgba(0,255,136,0.15)', color: '#86ffc7', border: '1px solid rgba(0,255,136,0.3)' }}>
                Protect
              </span>
            ) : (
              <span className="text-[11px] text-white/20 font-mono flex-shrink-0">{test.shortcut}</span>
            )}
          </div>
          <p className="text-[11px] text-white/30 truncate mt-0.5 leading-snug">{test.description}</p>
        </div>
      </button>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-[60px] bottom-0 z-[60] md:z-30 flex flex-col transition-[transform,width] duration-300
        w-[272px] ${isCollapsed ? 'md:w-[68px]' : 'md:w-[272px]'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      style={{
        background: 'rgba(10, 10, 15, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Top control: collapse (desktop) / close (mobile) */}
      <div className="px-3 pt-3 pb-2">
        {/* Desktop collapse toggle */}
        <button
          onClick={onToggleCollapse}
          className="hidden md:flex w-full items-center justify-center p-2 rounded-xl transition-all duration-200 group"
          style={{ border: '1px solid transparent' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-white/30 group-hover:text-white/60 transition-all duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="md:hidden w-full flex items-center justify-between px-2 py-1.5 rounded-xl text-white/50 hover:text-white hover:bg-white/5 transition-colors"
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]">Tests</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Test list — scrollable */}
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        <div className="space-y-5">
          <div>
            <p className={`text-[9px] font-bold uppercase tracking-[0.2em] text-white/20 mb-2 px-3 ${isCollapsed ? 'block md:hidden' : 'block'}`}>
              Core Tests
            </p>
            <div className="space-y-1">{coreTests.map(renderTestButton)}</div>
          </div>

          <div>
            <p className={`text-[9px] font-bold uppercase tracking-[0.2em] text-white/20 mb-2 px-3 ${isCollapsed ? 'block md:hidden' : 'block'}`}>
              Extended Tests
            </p>
            <div className="space-y-1">{extendedTests.map(renderTestButton)}</div>
          </div>

          {/* Sponsored — directly below Extended Tests */}
          <div className={isCollapsed ? 'block md:hidden' : 'block'}>
            <AffiliateAd isCollapsed={false} />
          </div>
        </div>
      </div>
    </aside>
  );
}
